import { useEffect } from 'react'
import { ChronoDialog, Testable } from '@eventops/chronotrack-ctlive-mui'
import { createLogger } from '../../../../app/logger'
import { CheckinModalContent } from './CheckinModalContent'
import { ActionButtons } from './ActionButtons'
import useAppDispatch from '../../../../hooks/useAppDispatch'
import useAppSelector from '../../../../hooks/useAppSelector'
import {
  getDirtyBibSelector,
  getDirtyTagSelector,
  getDirtyInputSelector,
  getIsAlertNoBibNumberOpenSelector,
  getIsAlertNoTagNumberOpenSelector,
  getIsAlertUnsavedChangesOpenSelector,
  getPanelStateSelector,
  getSelectedEntryIdSelector,
  getSelectedEntrySelector,
  PanelState,
  setDirtyInput,
  setIsAlertNoBibNumberOpen,
  setIsAlertNoTagNumberOpen,
  setIsAlertUnsavedChangesOpen,
  setIsAlertMakeBibTagAvailableOpen,
  setPanelState,
  setSelectedEntry,
  getIsAlertMakeBibTagAvailableOpenSelector,
} from '../../checkinSlice'
import CheckinAlertDialog from '../../../../components/common/CheckinAlertDialog'
import useCheckInEntry from '../../hooks/useCheckInEntry'
import useUnCheckInEntry from '../../hooks/useUnCheckInEntry'
import useCheckinSettings from '../../hooks/useCheckinSettings'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'CheckinModal',
})

export type CheckinModalProps = Testable

const CheckinModal = (props: CheckinModalProps) => {
  const selectedEntry = useAppSelector(getSelectedEntrySelector)
  const dirtyInput = useAppSelector(getDirtyInputSelector)
  const isAlertUnsavedChangesOpen = useAppSelector(
    getIsAlertUnsavedChangesOpenSelector
  )
  const isAlertNoBibNumberOpen = useAppSelector(
    getIsAlertNoBibNumberOpenSelector
  )
  const isAlertNoTagNumberOpen = useAppSelector(
    getIsAlertNoTagNumberOpenSelector
  )
  const isAlertMakeBibTagAvailableOpen = useAppSelector(
    getIsAlertMakeBibTagAvailableOpenSelector
  )
  const panelState = useAppSelector(getPanelStateSelector)
  const selectedEntryId = useAppSelector(getSelectedEntryIdSelector)
  const dispatch = useAppDispatch()
  const checkInEntry = useCheckInEntry()
  const unCheckInEntry = useUnCheckInEntry()
  const { checkinAllSettings } = useCheckinSettings()
  const askForTag = checkinAllSettings.settings?.askForTag
  const dirtyBib = useAppSelector(getDirtyBibSelector)
  const dirtyTag = useAppSelector(getDirtyTagSelector)
  const hasBib = !!dirtyBib
  const hasTag = !!askForTag && !!dirtyTag
  const bibTag = hasBib && hasTag ? 'Bib and Tag' : hasTag ? 'Tag' : 'bib'
  const missingBibTag =
    isAlertNoBibNumberOpen && isAlertNoTagNumberOpen
      ? 'Bib and Tag'
      : isAlertNoTagNumberOpen
        ? 'Tag'
        : 'Bib'

  const onClose = () => {
    if (dirtyInput) {
      dispatch(setIsAlertUnsavedChangesOpen(true))
      dispatch(setPanelState(PanelState.EDIT))
    } else {
      dispatch(setSelectedEntry(undefined))
      dispatch(setPanelState(PanelState.HIDDEN))
      dispatch(setDirtyInput(false))
    }
  }

  const handleNoButtonNoNumberClick = () => {
    dispatch(setIsAlertNoBibNumberOpen(false))
    dispatch(setIsAlertNoTagNumberOpen(false))
  }

  const handleYesButtonNoNumberClick = () => {
    dispatch(setIsAlertNoBibNumberOpen(false))
    dispatch(setIsAlertNoTagNumberOpen(false))
    checkInEntry()
  }

  const handleNoButtonUnsavedChangesClick = () => {
    dispatch(setIsAlertUnsavedChangesOpen(false))
  }

  const handleNoButtonMakeBibTagAvailableClick = () => {
    dispatch(setIsAlertMakeBibTagAvailableOpen(false))
  }

  const handleRemoveBibTagClick = (removeBib: boolean) => {
    dispatch(setIsAlertMakeBibTagAvailableOpen(false))
    unCheckInEntry(removeBib)
  }

  const handleYesButtonUnsavedChangesClick = () => {
    dispatch(setIsAlertUnsavedChangesOpen(false))
    dispatch(setSelectedEntry(undefined))
    dispatch(setPanelState(PanelState.HIDDEN))
    dispatch(setDirtyInput(false))
  }

  useEffect(() => {
    if (selectedEntry) {
      dispatch(setPanelState(PanelState.EDIT))
    }
  }, [dispatch, selectedEntry])

  if (PanelState.HIDDEN === panelState) {
    return null
  }

  return (
    <>
      <CheckinAlertDialog
        open={isAlertNoBibNumberOpen || isAlertNoTagNumberOpen}
        text={
          <>
            {isAlertNoBibNumberOpen && <div>No Bib Number was entered.</div>}
            {isAlertNoTagNumberOpen && <div>No Tag Number was entered.</div>}
          </>
        }
        noAction={{
          label: 'Keep Editing',
          onClick: handleNoButtonNoNumberClick,
        }}
        yesActions={[
          { label: `Save without ${missingBibTag}`, onClick: handleYesButtonNoNumberClick },
        ]}
      />
      <CheckinAlertDialog
        open={isAlertUnsavedChangesOpen}
        title={'Unsaved changes'}
        text={
          askForTag
            ? 'Are you sure you want to close? Bib and tag number will not be saved.'
            : 'Are you sure you want to close? Bib number will not be saved.'
        }
        noAction={{
          label: 'No',
          onClick: handleNoButtonUnsavedChangesClick,
        }}
        yesActions={[
          {
            label: 'Yes',
            onClick: handleYesButtonUnsavedChangesClick,
          },
        ]}
      />
      <CheckinAlertDialog
        open={isAlertMakeBibTagAvailableOpen}
        title={`Would you like to remove the ${bibTag}?`}
        text={`Removing the ${bibTag} will make the ${bibTag} available and will be able to be assigned to other athletes`}
        noAction={{
          label: 'Cancel',
          onClick: handleNoButtonMakeBibTagAvailableClick,
        }}
        yesActions={[
          {
            label: `Yes, Remove ${bibTag} from athlete`,
            onClick: () => handleRemoveBibTagClick(true),
          },
          {
            label: `No, Athlete keeps ${bibTag}`,
            onClick: () => handleRemoveBibTagClick(false),
          },
        ]}
      />
      <ChronoDialog
        data-cy={props['data-cy']}
        title={'Edit Athlete Check-in'}
        actions={<ActionButtons onCloseCancel={onClose} />}
        scroll={'paper'}
        onClose={onClose}
        open={!!selectedEntryId}
        isAlertOpen={isAlertUnsavedChangesOpen || isAlertNoBibNumberOpen || isAlertNoTagNumberOpen}
      >
        <CheckinModalContent />
      </ChronoDialog>
    </>
  )
}

export default CheckinModal
